import React from 'react';
import { Skeleton, TableCell, TableRow, Box, Card, Stack } from '@mui/material';

interface TableSkeletonProps {
  /** Number of placeholder rows. */
  rows?: number;
  /** Must match the real table's column count. */
  columns: number;
}

/**
 * Placeholder rows for a loading table. Using a skeleton that matches the
 * final layout avoids the content jump a spinner causes.
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {Array.from({ length: columns }).map((__, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton
              variant="text"
              // Ragged widths read as content rather than a loading bar.
              width={colIndex === 0 ? '70%' : `${45 + ((rowIndex + colIndex) % 4) * 12}%`}
              height={20}
            />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

interface CardSkeletonProps {
  count?: number;
  height?: number;
}

/** Placeholder cards for grid layouts. */
export const CardSkeleton: React.FC<CardSkeletonProps> = ({ count = 3, height = 140 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <Card key={index} sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="55%" height={26} />
          <Skeleton variant="text" width="85%" />
          <Skeleton variant="rectangular" height={height - 90} sx={{ borderRadius: 1, mt: 1 }} />
        </Stack>
      </Card>
    ))}
  </>
);

/** Generic block placeholder for detail panels. */
export const BlockSkeleton: React.FC<{ lines?: number }> = ({ lines = 4 }) => (
  <Box>
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        width={index === lines - 1 ? '60%' : '100%'}
        height={20}
        sx={{ mb: 1 }}
      />
    ))}
  </Box>
);

export default TableSkeleton;
